import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { markDayFlowSession } from '@/core/sentry'
import { goalQueryOptions } from '@/features/journey/hooks/use-journey'
import { useUser } from '@/features/plan/hooks/use-plan-data'
import { DayCompletionState, DayFormat } from '@/generated/graphql'
import { setDayFlowAnalyticsPlanType } from '../analytics-plan-type'

// Resolves the active FLOW plan's goal and which day the Today tab should show.
//
// Mirrors iOS (DayFlowDataStore.loadCurrentDay): the current day is the SERVER's
// `currentDayId` — with `lastCompletedDayId` as fallback so a finished goal still
// shows a reviewable day — NOT a client-side openOn/timer derivation (that only
// drives the journey map's visual locking). The value is held in the goal-query
// cache: `refetchOnMount: false` keeps it stable across the remounts that opening
// a practice causes, so completing a day never yanks the hub forward. The day
// advances only when the goal query is explicitly refetched on tab entry
// (ProtectedLayout invalidates ['journey'] when entering Today/Map), exactly like
// iOS re-resolving on tab appearance.
export function useCurrentFlowDay() {
  const { data: userData, isLoading: userLoading } = useUser()
  const activePlan =
    userData?.me.availablePlans.find(
      (p) => p.id === userData?.me.activePlanId,
    ) ?? userData?.me.availablePlans[0]

  const isFlow = activePlan?.dayFormat === DayFormat.Flow
  const goalId = activePlan?.currentGoalId ?? null

  // Keep day-flow analytics tagged with the active plan (split metrics per plan);
  // on a FLOW plan, also tag the Sentry session and switch its replay off.
  const planCode = activePlan?.code
  useEffect(() => {
    setDayFlowAnalyticsPlanType(planCode)
    if (isFlow) markDayFlowSession(planCode)
  }, [isFlow, planCode])

  const { data: goal, isLoading: goalLoading } = useQuery({
    ...goalQueryOptions(isFlow ? goalId : null),
    // Hold the resolved day across practice-nav remounts; advancing is driven by
    // the explicit tab-entry invalidation, not by remounting.
    refetchOnMount: false,
  })

  const dayId =
    goal?.userProgress?.currentDayId ??
    goal?.userProgress?.lastCompletedDayId ??
    null

  // Until a new goal's first day unlocks, the current day is the last day of the
  // goal before it.
  const goalDays = goal?.segments.flatMap((s) => s.days) ?? []
  const prevGoalId = goalDays.some((d) => d.id === dayId)
    ? null
    : (goal?.userProgress?.prevGoalId ?? null)
  const { data: prevGoal, isLoading: prevGoalLoading } = useQuery({
    ...goalQueryOptions(prevGoalId),
    refetchOnMount: false,
  })
  const allDays = [
    ...(prevGoal?.segments.flatMap((s) => s.days) ?? []),
    ...goalDays,
  ]
  const curIdx = allDays.findIndex((d) => d.id === dayId)

  // Day-level summary straight from the goal query — number/title/completion are
  // known before the (heavier) day-content query resolves, so the Today tab can
  // paint the day-open welcome immediately instead of flashing a skeleton while
  // the content loads.
  const rawDay = allDays.find((d) => d.id === dayId)
  const currentDay = rawDay
    ? {
        number: rawDay.number,
        title: rawDay.title,
        completionState:
          rawDay.userProgress?.completionState ?? DayCompletionState.NotStarted,
      }
    : null

  // Countdown to the next day unlocking — shown under "That's all for today"
  // once the current day is done. Uses the next day's real `openOn`, exactly
  // like the journey map's timer (deriveDayState). Null unless that day is
  // still locked with a future openOn (otherwise there's nothing to count to).
  const nextRawDay = curIdx >= 0 ? allDays[curIdx + 1] : undefined
  const nextOpenMs = nextRawDay?.userProgress?.openOn
    ? Date.parse(nextRawDay.userProgress.openOn)
    : Number.NaN
  const prevCompletedMs = rawDay?.userProgress?.completedAt
    ? Date.parse(rawDay.userProgress.completedAt)
    : Number.NaN
  const nowMs = Date.now()
  let nextDayTimer: {
    unlocksAtEpochMillis: number
    totalSeconds: number
  } | null = null
  if (
    nextRawDay &&
    !nextRawDay.userProgress?.isCompleted &&
    !Number.isNaN(nextOpenMs) &&
    nextOpenMs > nowMs
  ) {
    const remaining = Math.max(0, Math.floor((nextOpenMs - nowMs) / 1000))
    nextDayTimer = {
      unlocksAtEpochMillis: nextOpenMs,
      // Full wait from when the current day was completed, like deriveDayState.
      totalSeconds: Number.isNaN(prevCompletedMs)
        ? remaining
        : Math.max(
            Math.floor((nextOpenMs - prevCompletedMs) / 1000),
            remaining,
          ),
    }
  }

  // The loaded journey, oldest first — the step arm reads its windows off it.
  const journeyGoals = [prevGoal, goal].filter(
    (g): g is NonNullable<typeof g> => g != null,
  )

  return {
    isFlow,
    activePlan,
    dayId,
    currentDay,
    nextDayTimer,
    journeyGoals,
    goalId,
    isLoading: userLoading || (isFlow && (goalLoading || prevGoalLoading)),
  }
}
